import React, { Suspense, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { LuArrowUpRight } from "react-icons/lu";

import brk from "../../util/breakpoints";
import { orbitalGlow } from "../../util/glow";
import { prefersReducedMotion } from "../../util/motion";
import Container from "../Container";
import Button from "../Button";
import StatusChip from "../StatusChip";
import TypedWords from "../TypedWords";
import SceneBoundary from "../3JS/SceneBoundary";

// Loaded separately so three.js stays out of the main bundle
const HeroScene = React.lazy(() => import("../3JS/HeroScene"));

// What the headline cycles through after "building"
const buildWords = ["games", "apps", "AI tools", "products"];

const stars = [
  { left: "7.6%", top: "19.3%", size: 2, color: "var(--textDim)", d: 0 },
  { left: "34%", top: "13.7%", size: 3, color: "var(--purple)", d: 1.2 },
  { left: "55.2%", top: "23.9%", size: 2, color: "var(--lime)", d: 2.1 },
  { left: "92.4%", top: "12.8%", size: 3, color: "var(--text)", d: 0.6 },
  { left: "47.9%", top: "78.3%", size: 2, color: "var(--textDim)", d: 1.7 },
];

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

const Wrapper = styled.section`
  position: relative;
  display: flex;
  min-height: clamp(720px, 100vh, 1080px);
  padding-top: var(--navHeight);
  background-color: var(--bg);
  background-image: ${orbitalGlow};
  background-size: 100% 100%;
  overflow: hidden;
  @media ${brk.device.tablet} {
    min-height: clamp(600px, 100vh, 900px);
  }
`;

const Star = styled.span`
  position: absolute;
  left: ${(props) => props.$left};
  top: ${(props) => props.$top};
  width: ${(props) => `${props.$size}px`};
  height: ${(props) => `${props.$size}px`};
  border-radius: 50%;
  background: ${(props) => props.$color};
  animation: ${twinkle} 4s ease-in-out infinite;
  animation-delay: ${(props) => `${props.$d}s`};
`;

// Where the tiny world and its orbits sit. The 3D scene reads this element's position and size every frame,
// so moving or resizing it here is all it takes to move the world.
const WorldSlot = styled.div`
  position: absolute;
  right: -28px;
  top: 82px;
  width: 520px;
  height: 520px;
  pointer-events: none;
  @media ${brk.device.laptop} {
    position: relative;
    right: auto;
    top: auto;
    flex-shrink: 0;
    width: 280px;
    height: 280px;
    margin: 2.5rem auto 0;
  }
`;

// Static stand-in for the orbits, shown until the 3D scene is running
const Orbit = styled.span`
  position: absolute;
  inset: ${(props) => props.$inset};
  border-radius: 50%;
  border: 1px solid ${(props) => props.$color};
`;

const SceneBox = styled.div`
  position: absolute;
  inset: 0;
  opacity: ${(props) => (props.$on ? 1 : 0)};
  transition: opacity 1s;
`;

// Lets pointer input through to the scene behind it; the copy turns it back on for itself
const Content = styled(Container)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  pointer-events: none;
`;

const Copy = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 28px;
  max-width: 860px;
  padding: 3rem 0;
  pointer-events: auto;
  @media ${brk.device.laptop} {
    padding-top: 1.5rem;
  }
  > * {
    animation: ${fadeUp} 0.6s ease-out both;
  }
  > *:nth-child(2) {
    animation-delay: 0.08s;
  }
  > *:nth-child(3) {
    animation-delay: 0.16s;
  }
  > *:nth-child(4) {
    animation-delay: 0.24s;
  }
  > *:nth-child(5) {
    animation-delay: 0.32s;
  }
`;

const Marker = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--fontMono);
  font-size: 10px;
  white-space: nowrap;
  span {
    color: var(--lime);
  }
  i {
    width: 48px;
    height: 1px;
    background: var(--borderStrong);
  }
  p {
    color: var(--textDim);
  }
`;

const Headline = styled.h1`
  color: var(--text);
  font-family: var(--fontHeading);
  font-size: clamp(36px, 8vw, 78px);
  font-weight: 700;
  line-height: 0.98;
  > span {
    display: block;
    color: var(--lime);
  }
`;

const Introduction = styled.p`
  max-width: 760px;
  color: var(--textMuted);
  font-family: var(--fontBody);
  font-size: 18px;
  line-height: 1.55;
  @media ${brk.device.tablet} {
    font-size: 16px;
  }
`;

const Credibility = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  p {
    color: var(--textDim);
    font-family: var(--fontBody);
    font-size: 12px;
  }
`;

function Hero() {
  // The 3D scene replaces the static stars and orbits once it's running; they stay as the fallback.
  const [sceneAllowed, setSceneAllowed] = useState(() => !prefersReducedMotion());
  const [sceneReady, setSceneReady] = useState(false);
  const showStatic = !sceneAllowed || !sceneReady;
  const slotRef = useRef(null);

  return (
    <Wrapper id="home">
      {showStatic &&
        stars.map((star, index) => {
          return (
            <Star
              key={index}
              $left={star.left}
              $top={star.top}
              $size={star.size}
              $color={star.color}
              $d={star.d}
            />
          );
        })}
      {sceneAllowed && (
        <SceneBox $on={sceneReady}>
          <SceneBoundary
            onError={() => {
              setSceneAllowed(false);
            }}
          >
            <Suspense fallback={null}>
              <HeroScene
                slot={slotRef}
                onReady={() => {
                  setSceneReady(true);
                }}
              />
            </Suspense>
          </SceneBoundary>
        </SceneBox>
      )}
      <Content>
        <WorldSlot ref={slotRef}>
          {showStatic && (
            <>
              <Orbit $inset="0" $color="rgba(142, 114, 255, 0.2)" />
              <Orbit $inset="17.9%" $color="rgba(184, 242, 61, 0.157)" />
            </>
          )}
        </WorldSlot>
        <Copy>
          <Marker>
            <span>PLAYER 01</span>
            <i />
            <p>WEB · MOBILE · GAMES</p>
          </Marker>
          <Headline>
            Software engineer{" "}
            <span>
              building <TypedWords words={buildWords} />
            </span>{" "}
            with feeling.
          </Headline>
          <Introduction>
            I’m James Spears, a software engineer at Atlassian. I build web,
            mobile and game experiences that are solid underneath and enjoyable
            to use.
          </Introduction>
          <Button href="#work" icon={<LuArrowUpRight size={16} />}>
            Explore work
          </Button>
          <Credibility>
            <StatusChip>Currently · Atlassian</StatusChip>
            <p>Building collaboration software used by teams around the world.</p>
          </Credibility>
        </Copy>
      </Content>
    </Wrapper>
  );
}

export default Hero;
